import type { GetApiV1ThreeLetterCodePartnerAdminsNotifications200 } from '@fukuri-admins/apis/client/gen/model';
import { convertDateTime } from '~/utils/dateTime';
import type { PushNotificationListPresentation } from '../../presentations';

type PresentationProps = React.ComponentProps<typeof PushNotificationListPresentation>;

type Args = {
  /** API から取得したプッシュ通知一覧データ */
  notificationsData: GetApiV1ThreeLetterCodePartnerAdminsNotifications200;
};

type IConvertToPresentationProps = (args: Args) => {
  rows: PresentationProps['rows'];
  /** プッシュ通知が 0 件かどうか */
  isEmpty: boolean;
};

/** 指定文字数を超える文字列の末尾を「...」で省略する（現行画面のトランケート仕様に合わせる） */
const truncate = (value: string, maxLength: number): string => {
  // 絵文字（サロゲートペア）を途中で切らないよう、コードポイント単位で数える
  const chars = Array.from(value);
  return chars.length > maxLength ? `${chars.slice(0, maxLength).join('')}...` : value;
};

/**
 * API 型（snake_case）を PushNotificationList Presentation の props 型（camelCase）に変換する。
 *
 * - 出力型は `PushNotificationListPresentation` の Props から導出し、型乖離を防ぐ
 * - タイトル / URL は25文字、本文は10文字を超える場合に末尾を「...」で省略する
 * - target_user_count は 0 件を '-'、それ以外を文字列化した表示整形済みの値で渡す
 * - isEmpty フラグを導出して Container 層での表示出し分けに使用する
 */
export const convertToPresentationProps: IConvertToPresentationProps = ({ notificationsData }) => {
  const notifications = notificationsData.notifications;

  const rows: PresentationProps['rows'] = notifications.map(notification => ({
    id: notification.id,
    title: truncate(notification.title, 25),
    body: truncate(notification.body, 10),
    linkUrl: notification.link_url ? truncate(notification.link_url, 25) : '-',
    groupName: notification.group?.name ?? '-',
    targetUserCount:
      notification.target_user_count === 0 ? '-' : String(notification.target_user_count),
    deliverAt: convertDateTime(notification.deliver_at),
    isDelivered: new Date(notification.deliver_at * 1000) <= new Date(),
  }));

  const isEmpty = notifications.length === 0;

  return { rows, isEmpty };
};
